import { z } from 'zod';

import { TimeZoneSchema } from '../geo/time_zone.js';

/**
 * Contrato da sugestão de destino de corrida (spec 2026-10-13 recommendations). Viaja
 * web-chat → BFF → recommendation-api. As coordenadas do viajante vão SEMPRE no corpo
 * (POST): o pino-http loga a URL inteira.
 */
const Latitude = z.number().min(-90).max(90);
const Longitude = z.number().min(-180).max(180);

/**
 * Onde a lista aparece: a Home e a aba Ride da viagem pedem `home` (3 itens); a folha "Pedir
 * corrida", por qualquer entrada, pede `sheet` (até 8). Ausente, o pedido é de um bundle antigo
 * e a resposta sai no formato de 13/10 (spec 2026-10-15, Compatibilidade).
 */
export const RideSuggestionSurfaceSchema = z.enum(['home', 'sheet']);
export type RideSuggestionSurface = z.infer<typeof RideSuggestionSurfaceSchema>;

export const RideSuggestionsRequestSchema = z
  .object({
    latitude: Latitude,
    longitude: Longitude,
    /** O instante da consulta, com offset. */
    at: z.iso.datetime({ offset: true }),
    /** O fuso IANA do aparelho — a hora local do ranking sai daqui. */
    timeZone: TimeZoneSchema,
    surface: RideSuggestionSurfaceSchema.optional(),
    /** A viagem de onde a folha ou a aba Ride foi aberta: o servidor sugere os hotéis dela. */
    context: z
      .object({ tripId: z.string().min(1).max(100) })
      .strict()
      .optional(),
  })
  .strict();
export type RideSuggestionsRequest = z.infer<typeof RideSuggestionsRequestSchema>;

/**
 * Quando um lugar vem de mais de uma fonte, o tipo segue a natureza dele:
 * `airport` > `hotel` > `favorite` > `frequent` > `recent`.
 */
export const RideSuggestionKindSchema = z.enum([
  'airport',
  'hotel',
  'favorite',
  'frequent',
  'recent',
]);
export type RideSuggestionKind = z.infer<typeof RideSuggestionKindSchema>;

export const RideSuggestionReasonCodeSchema = z.enum([
  'usual_at_this_time',
  'after_arrival',
  'next_flight',
  'current_stay',
  'trip_hotel',
]);
export type RideSuggestionReasonCode = z.infer<typeof RideSuggestionReasonCodeSchema>;

export const RideSuggestionSchema = z
  .object({
    placeId: z.string().min(1),
    kind: RideSuggestionKindSchema,
    /** Aeroporto: o nome popular ("Guarulhos"); o cliente compõe "Aeroporto de …". */
    label: z.string().min(1),
    address: z.string().nullable(),
    latitude: Latitude,
    longitude: Longitude,
    /** Uso interno (ordem, selo, telemetria) — nunca exibida ao viajante. */
    probability: z.number().min(0).max(1),
    recommended: z.boolean(),
    reason: z.object({ code: RideSuggestionReasonCodeSchema }).strict().nullable(),
  })
  .strict();
export type RideSuggestion = z.infer<typeof RideSuggestionSchema>;

export const RideSuggestionsResponseSchema = z
  .object({
    suggestionSetId: z.string().min(1),
    rankedBy: z.enum(['jev', 'deterministic']),
    /** 3 na `home`, até 8 na `sheet`; sem `surface`, nunca mais de 3. */
    suggestions: z.array(RideSuggestionSchema).max(8),
  })
  .strict();
export type RideSuggestionsResponse = z.infer<typeof RideSuggestionsResponseSchema>;

export function EMPTY_RIDE_SUGGESTIONS(suggestionSetId: string): RideSuggestionsResponse {
  return { suggestionSetId, rankedBy: 'deterministic', suggestions: [] };
}
